import { useRef, useState } from 'react';
import { dateVersIso, isoVersDate } from '../../api/mappers';
import { piecesApi } from '../../api/resources';
import { ouvrePiece } from '../../components/DocumentsJoints.jsx';
import { Card } from '../../components/ui/Card.jsx';
import {
  attestationsAttendues,
  dernieresParSorte,
  etatAttestation,
  formeDeLaListe,
  PREAVIS_JOURS,
  sansDateDeFin,
} from '../../lib/attestations';
import { tr, trf } from '../../lib/i18n';
import { reduitLImage } from '../../lib/image';
import { C } from '../../theme/palette';
import { EnvoiAttestations } from './EnvoiAttestations.jsx';

const FORMES = ['Indépendant', 'Sàrl', 'SA'];

/** « 31122030 » se lit « 31.12.2030 » pendant la frappe. */
function saisieDate(texte) {
  const c = String(texte || '')
    .replace(/[^0-9]/g, '')
    .slice(0, 8);
  return [c.slice(0, 2), c.slice(2, 4), c.slice(4)].filter(Boolean).join('.');
}

/** Vrai pour une vraie date « JJ.MM.AAAA » (pas le 31.02). */
function dateReelle(texte) {
  const iso = dateVersIso(texte);
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const d = new Date(iso + 'T00:00:00Z');
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === iso;
}

/** La pastille d'état d'une attestation, à droite de sa ligne. */
function Pastille({ etat }) {
  const styles = {
    ok: [C.green, '#e7f7ee'],
    bientot: [C.amber, '#fff4dc'],
    expiree: [C.red, '#fdecec'],
    manquant: [C.red, '#fdecec'],
    facultatif: [C.muted, '#f1f4f8'],
  };
  const [couleur, fond] = styles[etat.etat] || styles.facultatif;
  const texte =
    etat.etat === 'ok'
      ? etat.fin
        ? '✓ ' + trf('jusqu’au {0}', isoVersDate(etat.fin))
        : '✓ ' + tr('reçu')
      : etat.etat === 'bientot'
        ? '⚠ ' + trf('expire dans {0} j', etat.jours)
        : etat.etat === 'expiree'
          ? '⚠ ' + trf('fini le {0}', isoVersDate(etat.fin))
          : etat.etat === 'manquant'
            ? tr('manquant')
            : tr('facultatif');
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 800,
        color: couleur,
        background: fond,
        borderRadius: 7,
        padding: '3px 7px',
        whiteSpace: 'nowrap',
      }}
    >
      {texte}
    </span>
  );
}

/**
 * « Attestations de l'entreprise » : ce que la fiduciaire, une régie ou un
 * contrôle demandent — RC entreprise, statut d'indépendant, TVA, poursuites…
 * pour une raison individuelle ; registre du commerce, statuts, comptes
 * annuels, LPP… pour une Sàrl ou une SA. Chaque pièce se joint en photo ou en
 * PDF, avec sa date de fin : 30 jours avant, la cloche prévient.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.soc l'entreprise (son id, sa forme juridique)
 * @param {object[]} props.pieces les pièces déjà jointes à l'entreprise
 * @param {Function} props.onChange relit les pièces après un ajout ou un retrait
 * @param {object[]} [props.clients] les clients, à qui envoyer les attestations cochées
 * @returns {JSX.Element} la carte du classeur
 */
export function AttestationsEntreprise({ soc, pieces, onChange, clients }) {
  // La forme de Réglages, tant qu'on n'en regarde pas une autre : la fiche
  // de l'entreprise arrive parfois après l'écran.
  const [formeVue, setForme] = useState(null);
  const forme = formeVue || formeDeLaListe(soc?.forme);
  // L'ajout en cours : la sorte choisie, le fichier, puis la date de fin.
  const [ajout, setAjout] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const choix = useRef(null);
  const sorteVoulue = useRef(null);
  // Les attestations cochées, à envoyer ensemble à une régie, un client… ;
  // puis la fenêtre d'envoi.
  const [coches, setCoches] = useState([]);
  const [envoiOuvert, setEnvoiOuvert] = useState(false);

  const attendues = attestationsAttendues(forme);
  const dernieres = dernieresParSorte(pieces);
  const parSorte = new Map(dernieres.map((p) => [p.categorie, p]));
  const autres = (pieces || []).filter((p) => p.categorie === 'attestation');
  const recues = attendues.filter((a) => parSorte.has(a.cle)).length + autres.length;
  // Seules les attestations montrées ici s'envoient : une attestation retirée,
  // ou remplacée par une nouvelle version, n'est plus cochée ; une pièce sans
  // fichier ne s'envoie pas.
  const envoyables = [...attendues.map((a) => parSorte.get(a.cle)), ...autres].filter(
    (p) => p && p.fichier,
  );
  const choisies = coches.map((id) => envoyables.find((p) => p.id === id)).filter(Boolean);
  const bascule = (id) =>
    setCoches((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  // La case à gauche d'une attestation reçue : la cocher la met dans l'envoi.
  const caseAEnvoyer = (piece, nom) =>
    piece && piece.fichier ? (
      <input
        type="checkbox"
        aria-label={nom}
        checked={coches.includes(piece.id)}
        onChange={() => bascule(piece.id)}
        style={{ width: 20, height: 20, flexShrink: 0, accentColor: C.teal, margin: 0 }}
      />
    ) : (
      <span style={{ width: 20, flexShrink: 0 }} />
    );

  const choisit = (sorte) => {
    sorteVoulue.current = sorte;
    setErreur('');
    if (choix.current) {
      choix.current.value = '';
      choix.current.click();
    }
  };

  const fichierChoisi = (e) => {
    const fichier = e.target.files && e.target.files[0];
    if (!fichier) return;
    const sorte = sorteVoulue.current;
    setAjout({
      sorte,
      fichier,
      fin: '',
      nom: sorte === 'attestation' ? fichier.name.replace(/\.[a-z0-9]+$/i, '') : '',
    });
  };

  const enregistre = async () => {
    if (!ajout) return;
    setEnvoi(true);
    setErreur('');
    try {
      const { fichier, sorte } = ajout;
      const pret = fichier.type.startsWith('image/') ? await reduitLImage(fichier) : fichier;
      const attendue = attendues.find((a) => a.cle === sorte);
      const libelle = attendue ? tr(attendue.lib) : ajout.nom || tr('Attestation');
      await piecesApi.depose('ENTREPRISE', soc.id, pret, libelle, {
        categorie: sorte,
        date: (!sansDateDeFin(sorte) && dateVersIso(ajout.fin)) || undefined,
      });
      setAjout(null);
      onChange && onChange();
    } catch (err) {
      setErreur(err?.message || tr('Le document n’a pas pu être joint.'));
    } finally {
      setEnvoi(false);
    }
  };

  const retire = async (piece) => {
    if (!window.confirm(trf('Retirer « {0} » ?', piece.libelle || tr('Attestation')))) return;
    try {
      await piecesApi.retire(piece.id);
      onChange && onChange();
    } catch (err) {
      setErreur(err?.message || tr('Le document n’a pas pu être retiré.'));
    }
  };

  const petitBouton = {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid ' + C.border,
    background: '#fff',
    fontSize: 10,
    fontWeight: 800,
    color: C.ink,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  // Une assurance, un extrait des poursuites ont une date de fin : elle est
  // demandée. Un document « autre » la prend s'il en a une.
  const finDemandee = !!ajout && ajout.sorte !== 'attestation' && !sansDateDeFin(ajout.sorte);
  const finValide = dateReelle(ajout?.fin);

  // La date de fin, demandée juste après le choix du fichier.
  const saisieFin = (
    <div
      style={{
        border: '1px solid ' + C.teal,
        borderRadius: 10,
        padding: 10,
        margin: '6px 0 10px',
        background: '#f3fafb',
      }}
    >
      <div style={{ fontSize: 10.5, fontWeight: 800, color: C.ink, marginBottom: 6 }}>
        📎 {ajout?.fichier?.name}
      </div>
      {ajout?.sorte === 'attestation' && (
        <label style={{ display: 'block', fontSize: 9.5, fontWeight: 700, color: C.muted }}>
          {tr('Ce que c’est')}
          <input
            value={ajout.nom}
            onChange={(e) => setAjout({ ...ajout, nom: e.target.value })}
            style={{
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
              margin: '4px 0 8px',
              padding: '7px 8px',
              borderRadius: 8,
              border: '1px solid ' + C.border,
              fontSize: 12,
            }}
          />
        </label>
      )}
      {/* Le registre du commerce, les statuts… n'ont pas de date de fin : rien
          à demander. Une assurance, un extrait des poursuites en ont une : la
          case est là tout de suite, et à remplir. Un champ texte « JJ.MM.AAAA »
          plutôt que le calendrier du téléphone, qui affichait le jour même
          dans un champ vide. */}
      {sansDateDeFin(ajout?.sorte) ? (
        <div style={{ fontSize: 9.5, color: C.grey, margin: '2px 0 8px', lineHeight: 1.45 }}>
          ♾ {tr('Pas de date de fin : valable tant que l’entreprise existe.')}
        </div>
      ) : finDemandee || ajout?.avecFin ? (
        <label style={{ display: 'block', fontSize: 9.5, fontWeight: 700, color: C.muted }}>
          {tr('Valable jusqu’au')}
          <input
            inputMode="numeric"
            placeholder={tr('JJ.MM.AAAA')}
            aria-label={tr('Valable jusqu’au')}
            value={ajout?.fin || ''}
            onChange={(e) => setAjout({ ...ajout, fin: saisieDate(e.target.value) })}
            style={{
              display: 'block',
              width: 130,
              margin: '4px 0 8px',
              padding: '7px 8px',
              borderRadius: 8,
              border: '1px solid ' + (finDemandee && !finValide ? C.amber : C.border),
              fontSize: 13,
            }}
          />
        </label>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '2px 0 8px' }}>
          <span style={{ fontSize: 9.5, color: C.grey }}>{tr('Pas de date de fin')}</span>
          <button
            type="button"
            onClick={() => setAjout({ ...ajout, avecFin: true })}
            style={petitBouton}
          >
            📅 {tr('Ajouter la date de fin')}
          </button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          disabled={envoi || (finDemandee && !finValide)}
          onClick={enregistre}
          style={{
            ...petitBouton,
            background: C.navy,
            color: '#fff',
            border: 'none',
            opacity: envoi || (finDemandee && !finValide) ? 0.4 : 1,
          }}
        >
          {envoi ? tr('Envoi…') : '✓ ' + tr('Enregistrer')}
        </button>
        <button type="button" onClick={() => setAjout(null)} style={petitBouton}>
          {tr('Annuler')}
        </button>
      </div>
    </div>
  );

  return (
    <Card style={{ marginBottom: 6, overflow: 'hidden' }}>
      <details>
        <summary
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            cursor: 'pointer',
            listStyle: 'none',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: C.teal + '18',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              flexShrink: 0,
            }}
          >
            🏢
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800 }}>
              {tr('Attestations de l’entreprise')}
            </div>
            <div style={{ fontSize: 8.5, color: C.muted }}>
              {tr('Ce que la fiduciaire, une régie ou un contrôle demandent')} ·{' '}
              {trf('{0} document(s)', recues)}
            </div>
          </div>
          <div style={{ fontSize: 10, fontWeight: 800, color: C.teal }}>▸ {tr('voir')}</div>
        </summary>
        <div style={{ padding: '0 14px 12px' }}>
          <input
            ref={choix}
            type="file"
            accept="image/*,application/pdf"
            onChange={fichierChoisi}
            style={{ display: 'none' }}
            data-testid="attestation-fichier"
          />
          <div role="group" aria-label={tr('Forme juridique')} style={{ display: 'flex', gap: 6 }}>
            {FORMES.map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={forme === f}
                onClick={() => setForme(f)}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  borderRadius: 9,
                  fontSize: 10.5,
                  fontWeight: 800,
                  cursor: 'pointer',
                  border: '1px solid ' + (forme === f ? C.navy : C.border),
                  background: forme === f ? C.navy : '#fff',
                  color: forme === f ? '#fff' : C.grey,
                }}
              >
                {tr(f)}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 9, color: C.grey, margin: '6px 0 4px' }}>
            {tr('Choisie dans Réglages. La liste change selon la forme.')}
          </div>
          {envoyables.length > 0 && (
            <div style={{ fontSize: 9, color: C.teal, fontWeight: 700, margin: '0 0 4px' }}>
              ☑ {tr('Coche les documents à envoyer à une régie, un client…')}
            </div>
          )}
          {attendues.map((a) => {
            const piece = parSorte.get(a.cle);
            const etat = etatAttestation(piece, a.niveau);
            const enAjout = ajout && ajout.sorte === a.cle;
            return (
              <div key={a.cle} style={{ borderTop: '1px solid #eef2f6', padding: '9px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {caseAEnvoyer(piece, tr(a.lib))}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 800, color: C.ink }}>
                      {a.icon} {tr(a.lib)}
                    </div>
                    {a.aide && (
                      <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{tr(a.aide)}</div>
                    )}
                  </div>
                  {etat.etat !== 'ajouter' && <Pastille etat={etat} />}
                  {!piece && (
                    <button type="button" onClick={() => choisit(a.cle)} style={petitBouton}>
                      ＋ {tr('Ajouter')}
                    </button>
                  )}
                </div>
                {piece && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => ouvrePiece(piece)} style={petitBouton}>
                      📄 {tr('Voir')}
                    </button>
                    <button type="button" onClick={() => choisit(a.cle)} style={petitBouton}>
                      ↻ {tr('Nouvelle version')}
                    </button>
                    <button
                      type="button"
                      onClick={() => retire(piece)}
                      aria-label={tr('Retirer')}
                      style={{ ...petitBouton, color: C.red }}
                    >
                      ✕
                    </button>
                  </div>
                )}
                {enAjout && saisieFin}
              </div>
            );
          })}
          {autres.map((p) => (
            <div
              key={p.id}
              style={{
                borderTop: '1px solid #eef2f6',
                padding: '9px 0',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {caseAEnvoyer(p, p.libelle || tr('Attestation'))}
              <div style={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 800 }}>
                📄 {p.libelle}
              </div>
              <Pastille etat={etatAttestation(p, 'conseille')} />
              <button type="button" onClick={() => ouvrePiece(p)} style={petitBouton}>
                {tr('Voir')}
              </button>
              <button
                type="button"
                onClick={() => retire(p)}
                aria-label={tr('Retirer')}
                style={{ ...petitBouton, color: C.red }}
              >
                ✕
              </button>
            </div>
          ))}
          {ajout && ajout.sorte === 'attestation' && saisieFin}
          {choisies.length > 0 && (
            <button
              type="button"
              onClick={() => setEnvoiOuvert(true)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginTop: 10,
                padding: '12px 14px',
                borderRadius: 12,
                border: 'none',
                background: C.navy,
                color: '#fff',
                fontSize: 12.5,
                fontWeight: 900,
                cursor: 'pointer',
              }}
            >
              <span>✉️ {tr('Envoyer la sélection')}</span>
              <span
                style={{
                  background: '#fff',
                  color: C.navy,
                  borderRadius: 9,
                  padding: '3px 9px',
                  fontSize: 11.5,
                }}
              >
                {trf('{0} document(s)', choisies.length)}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={() => choisit('attestation')}
            style={{
              width: '100%',
              marginTop: 8,
              padding: '11px',
              borderRadius: 10,
              border: '1.5px dashed ' + C.teal,
              background: '#fff',
              color: C.teal,
              fontSize: 11,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            📷 ＋ {tr('Ajouter un autre document (photo ou PDF)')}
          </button>
          {erreur && (
            <div role="alert" style={{ fontSize: 10, color: C.red, marginTop: 8 }}>
              {erreur}
            </div>
          )}
          <div style={{ fontSize: 9, color: C.grey, marginTop: 8, lineHeight: 1.5 }}>
            {trf(
              'Indique la date de fin : {0} jours avant, la cloche 🔔 te prévient de la renouveler.',
              PREAVIS_JOURS,
            )}
          </div>
        </div>
      </details>
      {envoiOuvert && choisies.length > 0 && (
        <EnvoiAttestations
          soc={soc}
          pieces={choisies}
          clients={clients}
          onRetire={bascule}
          onClose={() => setEnvoiOuvert(false)}
          onEnvoye={() => setCoches([])}
        />
      )}
    </Card>
  );
}
